import React, { lazy, Suspense, Fragment } from 'react';
import { LicenseManager } from 'ag-grid-enterprise';
import ClearScripts from '@mummssoftware/clearscripts';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  ActionIcon,
  CardHeaderTab,
  HeightProviderWithWindowHeight,
  Loader,
  TopBar as ButtonAppBar,
  DialogBox,
} from '@mummssoftware/common-ui';
import { PNCounterContextProvider } from '@mummssoftware/global-context/PNCounter';
import { ENTITY_MEDICATION } from '@mummssoftware/global-context/PNCounter/constants';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';
import { RxModal } from './Components/RxModal';
import { withApprovalContext } from './Context/ApprovalContext';
import messages from './Components/Messages';
import { ApprovalContainer } from './Components/ApprovalContainer';
import { ReviewContainer } from './Components/reviews/ReviewContainer';
import defaults from '../../translations/en.json';
import {
  loadPatientVerbalApprovalsForPrint,
  loadBulkUnsignedVerbalsForPrint,
} from './Print/loadPatientVerbalApprovalsForPrint';
import { withReviewContext } from './Context/ReviewContext';
import { UnsignedVerbalsPrintFaxBridge } from './UnsignedVerbalsPrintFaxBridge';
import { BulkUnsignedVerbalsSummaryTable } from './Components/BulkUnsignedVerbalsSummaryTable';

LicenseManager && LicenseManager.setLicenseKey(window.env?.secrets?.agGridKey);

const { UnsignedOrders, UnsignedMeds } = defaults;

const CertListingGrid = lazy(() => import('./CertListingGrid'));
const ApprovalListingGrid = lazy(() => import('./ApprovalListingGrid'));
const ReviewListingGrid = lazy(() => import('./ReviewListingGrid'));

const modalMaxWidth = 1100;
const baseCertWrapStyle = {
  display: 'contents',
  height: '100%',
  maxWidth: modalMaxWidth,
};

const NavWrapper = styled.div`
  margin-right: 8px;
`;

const RxIconWrap = styled.div`
  margin-right: ${(props) => (props.isMobile ? '2px' : '15px')};
`;

const styles = (theme) => ({
  modalRoot: {
    marginTop: 'var(--safe-area-inset-top)',
    '& > * > *::-webkit-scrollbar-track, & > * > *::-webkit-scrollbar-corner': {
      background: `${theme.palette.background.default}ce`,
    },
    '@media print': {
      height: 'fit-content',
      top: 'unset !important',
      bottom: 'unset !important',
    },
  },
  modalRootMobile: {
    '& > * > *::-webkit-scrollbar-track, & > * > *::-webkit-scrollbar-corner': {
      background: theme.palette.background.header,
    },
    [theme.breakpoints.up('sm')]: {
      '@media print': {
        maxWidth: theme.props.isSafari ? 'calc(100% - 3cm)' : null,
      },
    },
  },
  modal: {
    maxWidth: modalMaxWidth,
    alignSelf: 'flex-start',
    background: `linear-gradient(to bottom, transparent 120px,${theme.palette.background.paper} 120px)`,
    width: '100%',
    isolation: 'isolate',
    [theme.breakpoints.only('xs')]: {
      maxHeight: 'calc(100vh - 3px)',
      margin: '2px 0',
    },
    '@media print': {
      background: theme.props.printRules.backgroundColor,
      margin: theme.props.printRules.bodyMargin,
      boxShadow: 'none',
    },
  },
  modalWidthFalse: {
    alignSelf: 'flex-start',
  },
  loaderPaper: {
    background: 'transparent',
  },
  openRoot: {
    height: '100vh',
    width: '100vw',
    top: 0,
    left: 0,
    position: 'absolute',
    filter: 'blur(1px)',
    touchAction: 'none',
    ...theme.props.printRules.hide,
  },
});

class Listing extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      certOpen: false,
      medOpen: false,
      ordOpen: false,
      selectedApprovalId: null,
      reviewOpen: false,
      rxModalOpen: false,
      certLoaded: false,
      certNav: false,
      approvalNav: true,
      reviewNav: true,
      rxLink: null,
      refresh: false,
      printReady: false,
      ordBulkOpen: false,
      bulkPhysicianKey: null,
      bulkPhysicianName: '',
      bulkPrintReady: false,
      loadedBulkVerbals: null,
      unsignedVerbalsPayload: null,
      printServiceBusy: false,
    };
  }

  static defaultProps = {
    isMobile: false,
    enableClinicalLinks: false,
  };

  headerRef = React.createRef();

  handleSelectCertTab = () => {
    this.setState({
      certNav: false,
      approvalNav: true,
      reviewNav: true,
    });
  };

  handleSelectApprovalTab = () => {
    this.setState({ certNav: true, approvalNav: false, reviewNav: true });
  };

  handleSelectReviewTab = () => {
    this.setState({ certNav: true, approvalNav: true, reviewNav: false });
  };

  handleCertClose = () => {
    const { resetVerbal } = this.props;
    resetVerbal && resetVerbal();
    this.setState({
      certOpen: false,
      certLoaded: false,
      fullScreenDialog: false,
    });
  };

  handleCertRefresh = () => {
    this.setState({
      refresh: true,
    });
  };

  handleMedClose = () => {
    this.setState({
      medOpen: false,
      ordOpen: false,
      ordBulkOpen: false,
      medTitle: null,
      ordTitle: null,
      selectedApprovalId: null,
      bulkPhysicianKey: null,
      bulkPhysicianName: '',
      bulkPrintReady: false,
      printReady: false,
      loadedBulkVerbals: null,
      unsignedVerbalsPayload: null,
      printServiceBusy: false,
    });
  };

  setPrintReady = (status) => {
    this.setState({
      printReady: status,
    });
  };

  setBulkPrintReady = (status) => {
    this.setState({ bulkPrintReady: status });
  };

  openPhysicianBulkPrint = ({ physicianKey, doctorName }) => {
    this.setState(
      {
        ordBulkOpen: true,
        bulkPhysicianKey: physicianKey,
        bulkPhysicianName: doctorName,
        bulkPrintReady: false,
        loadedBulkVerbals: null,
      },
      () => this.fetchBulkUnsignedVerbalsForPhysicianPrintDialog(),
    );
  };

  /**
   * After the nurse picks a physician in the bulk unsigned-verbals print flow,
   * calls `loadBulkUnsignedVerbalsForPrint` and stores the result in
   * `loadedBulkVerbals` / `bulkPrintReady` (or clears on error / stale dialog).
   */
  fetchBulkUnsignedVerbalsForPhysicianPrintDialog = async () => {
    const physicianKey = this.state.bulkPhysicianKey;
    if (physicianKey == null) return;
    const {
      workflow,
      observation,
      pim,
      hospiceBin,
      agency,
      siteId,
      compiledApprovals,
      workFlowApprovals,
    } = this.props;
    try {
      const unsignedVerbals = await loadBulkUnsignedVerbalsForPrint({
        physicianPersonId: physicianKey,
        compiledApprovals,
        workFlowApprovals,
        workflow,
        observation,
        pim,
        hospiceBin,
        agency,
        siteId,
      });
      if (
        !this.state.ordBulkOpen ||
        this.state.bulkPhysicianKey !== physicianKey
      ) {
        return;
      }
      this.setState({
        loadedBulkVerbals: unsignedVerbals,
        bulkPrintReady: true,
      });
    } catch (e) {
      if (
        !this.state.ordBulkOpen ||
        this.state.bulkPhysicianKey !== physicianKey
      ) {
        return;
      }
      this.setState({ loadedBulkVerbals: [], bulkPrintReady: true });
    }
  };

  loadSingleUnsignedVerbalsForPrint = async () => {
    const patientNumber = this.state.selectedPatientId;
    const physicianPersonId = this.state.selectedDoctor;
    // OP-31894: set when a single order was opened from the "By Order" view
    const approvalId = this.state.selectedApprovalId;
    const physicianMissing =
      physicianPersonId === null ||
      physicianPersonId === undefined ||
      String(physicianPersonId).trim() === '';
    if (!patientNumber || physicianMissing) return;
    const {
      workflow,
      observation,
      pim,
      hospiceBin,
      agency,
      siteId,
    } = this.props;
    try {
      const {
        printApprovals,
        patientInfo,
      } = await loadPatientVerbalApprovalsForPrint({
        patientNumber,
        physicianPersonId,
        approvalId,
        workflow,
        observation,
        pim,
        hospiceBin,
        agency,
        siteId,
      });
      if (
        !this.state.ordOpen ||
        this.state.selectedPatientId !== patientNumber ||
        this.state.selectedApprovalId !== approvalId
      ) {
        return;
      }
      const unsignedVerbals = (printApprovals || []).map((dto) => ({
        ...dto,
        patientInfo,
      }));
      this.setState({
        unsignedVerbalsPayload: unsignedVerbals,
        printReady: true,
      });
    } catch (e) {
      if (!this.state.ordOpen) return;
      this.setState({ unsignedVerbalsPayload: [], printReady: true });
    }
  };

  setUnsignedVerbalsPrintBusy = (busy) => {
    this.setState({ printServiceBusy: busy });
  };

  handleReviewClose = () => {
    this.setState({
      reviewOpen: false,
    });
    this.props.reloadReviewGrid();
  };

  openCertWindow = ({ event: { target }, data }) => {
    // sometimes target is actually an svg path, so check the parent element as well
    const isExternalLink =
      target.attributes['external-link'] ||
      target.parentElement.attributes['external-link'];

    if (!isExternalLink) {
      this.setState({
        certOpen: true,
        fullScreenDialog: true,
        selectedPatientId: data.patientNumber,
        selectedPatientCarrierId: data.insuranceCarrierId,
        selectedPatientElectionNumber: data.electionNumber,
      });
    }
  };

  openRxModal = async (action) => {
    const rxLink = (await this.getExternalURL(action)) || {};
    this.setState({
      rxModalOpen: true,
      rxLink,
    });
  };

  closeRxModal = () => {
    this.setState({
      rxModalOpen: false,
    });
  };

  getExternalURL = async (action) => {
    const url = await this.props.clearScripts.getRxExternalUrl(
      this.props.siteNickname,
      this.props.agency,
      action,
    );
    const response = {};
    response.status = url.status;
    if (url.status === 403) {
      response.message = <FormattedMessage {...messages.notSetup} />;
      return response;
    }
    response.url = await url?.text();
    return response;
  };

  onCertLoad = () => {
    this.setState({ certLoaded: true, fullScreenDialog: false });
  };

  // data === AgGrid data
  openCellWindow = (data) => {
    // data.data exposes the row's contents
    const patient = data.data;
    const {
      patientFirstname,
      patientLastname,
      patientNumber,
      physicianPersonId,
    } = patient;
    // this is read-only view if not a physician or a nurse practitioner
    if (data.colDef.field === 'numUnreconciledMeds') {
      const unreconciledMedTitle = this.props.intl.formatMessage(UnsignedMeds, {
        patientFirstname,
        patientLastname,
        patientNumber,
      });
      this.setState({
        // refreshes virtual dom tree & rerenders the modal (now open)
        medOpen: true,
        selectedPatientId: patient.patientNumber,
        medTitle: unreconciledMedTitle,
        selectedDoctor: data.data.orderingphysicianid,
      });
    } else if (data.colDef.field === 'unreconciledOrders') {
      const unreconciledOrdersTitle = this.props.intl.formatMessage(
        UnsignedOrders,
        {
          patientFirstname,
          patientLastname,
          patientNumber,
        },
      );
      this.setState(
        {
          ordOpen: true,
          selectedPatientId: patient.patientNumber,
          ordTitle: unreconciledOrdersTitle,
          selectedDoctor:
            physicianPersonId === undefined
              ? data.data.orderingphysicianid
              : physicianPersonId,
          selectedApprovalId: null,
          printReady: false,
          unsignedVerbalsPayload: null,
        },
        () => this.loadSingleUnsignedVerbalsForPrint(),
      );
    }
  };

  // OP-31894: a row of the "By Order" view opens just that unsigned order
  openOrderWindow = ({ data }) => {
    const {
      patientFirstname,
      patientLastname,
      patientNumber,
      physicianPersonId,
      orderingphysicianid,
      approvalId,
    } = data;
    const unreconciledOrdersTitle = this.props.intl.formatMessage(
      UnsignedOrders,
      {
        patientFirstname,
        patientLastname,
        patientNumber,
      },
    );
    this.setState(
      {
        ordOpen: true,
        selectedPatientId: patientNumber,
        ordTitle: unreconciledOrdersTitle,
        selectedDoctor:
          physicianPersonId === undefined || physicianPersonId === null
            ? orderingphysicianid
            : physicianPersonId,
        selectedApprovalId: approvalId,
        printReady: false,
        unsignedVerbalsPayload: null,
      },
      () => this.loadSingleUnsignedVerbalsForPrint(),
    );
  };

  openReviewWindow = (data) => {
    const record = data.data;
    const { patientName, workerName } = record;
    const title = workerName ? `${patientName} — ${workerName}` : patientName;
    // this is read-only view if not a physician or a nurse practitioner
    this.setState({
      patientName: title,
      reviewOpen: true,
      selectedPatientNumber: record.patientNumber,
      selectedPatientId: record.patientId,
      selectedReviewsMapKey: record.reviewsMapKey,
    });
  };

  componentDidMount() {
    this.props.workflow.refreshReviews(this.props.agency);
  }

  render() {
    const {
      classes,
      PatientCert,
      // passed to PatientCert (AppContext)
      certContext,
      enableClinicalLinks,
      firstName,
      handleAgencyChange,
      handleSiteChange,
      hbcLocation,
      hospiceBin,
      hospiceName,
      isDoctor,
      isMobile,
      isMumms,
      lastName,
      logout,
      smartChartUrl,
      siteId,
      siteList,
      siteNickname,
      updateUserInfo,
      userId,
      userNumber,
      keycloak,
      rxmessage,
      rxsign,
      observation,
      agency,
      getAgency,
      currentUser,
    } = this.props;
    const { rxModalOpen, rxLink, refresh } = this.state;
    return (
      <div
        className={
          this.state.certOpen ||
          this.state.medOpen ||
          this.state.ordOpen ||
          this.state.ordBulkOpen
            ? classes.openRoot
            : ''
        }
      >
        <ButtonAppBar
          ref={this.headerRef}
          title={
            <Box display="flex" flexDirection="row">
              <NavWrapper>
                <CardHeaderTab
                  handleClick={this.handleSelectCertTab}
                  active={!this.state.certNav}
                  bottom={-2}
                >
                  {this.props.intl.formatMessage(messages.certTab)}
                </CardHeaderTab>
              </NavWrapper>
              <NavWrapper>
                <CardHeaderTab
                  handleClick={this.handleSelectApprovalTab}
                  active={!this.state.approvalNav}
                  bottom={-2}
                >
                  {this.props.intl.formatMessage(messages.approvalTab)}
                </CardHeaderTab>
              </NavWrapper>
              <NavWrapper>
                <CardHeaderTab
                  handleClick={this.handleSelectReviewTab}
                  active={!this.state.reviewNav}
                  bottom={-2}
                >
                  {this.props.intl.formatMessage(messages.reviewTab)}
                </CardHeaderTab>
              </NavWrapper>
            </Box>
          }
          titleInfo={`v${process.env.REACT_APP_VERSION}`}
          firstName={firstName}
          lastName={lastName}
          leftAction={
            <RxIconWrap isMobile={isMobile}>
              {rxmessage > 0 && (
                <ActionIcon
                  title={`Refills & Rx Changes: ${rxmessage}`}
                  htmlColor="#DC143C"
                  style={{
                    margin: 0,
                    'margin-right': 3,
                    width: 20,
                    height: 20,
                    boxShadow: '0 0 10px #DC143C',
                  }}
                  onClick={() => this.openRxModal('rxmessage')}
                  Icon={ErrorRoundedIcon}
                />
              )}
              {rxsign > 0 && (
                <ActionIcon
                  title={`Rx Pending & Sent: ${rxsign}`}
                  htmlColor="#DC143C"
                  style={{
                    margin: 0,
                    width: 20,
                    height: 20,
                    boxShadow: '0 0 10px #DC143C',
                  }}
                  onClick={() => this.openRxModal('rxsign')}
                  Icon={ErrorRoundedIcon}
                />
              )}
            </RxIconWrap>
          }
          siteList={siteList}
          currentAgency={
            /* eslint-disable indent */
            hospiceBin
              ? {
                  hospiceBin,
                  hospiceName,
                }
              : undefined
          }
          currentSite={{
            id: siteId,
            nickName: siteNickname,
          }}
          shadow={false}
          enableAgencyPicker={isMumms}
          logout={logout}
          onAgencyChange={handleAgencyChange}
          onSiteChange={handleSiteChange}
        />
        <HeightProviderWithWindowHeight passedRef={this.headerRef}>
          {({ windowHeight, height }) => (
            <Suspense fallback={<Loader />}>
              {!this.state.certNav && (
                <CertListingGrid
                  isDoctor={isDoctor}
                  certClosed={!this.state.medOpen && !this.state.certLoaded}
                  siteId={siteId}
                  userId={userId}
                  enableClinicalLinks={enableClinicalLinks}
                  hbcLocation={hbcLocation}
                  isMobile={isMobile}
                  smartChartUrl={smartChartUrl}
                  openCertWindow={this.openCertWindow}
                  windowHeight={windowHeight}
                  height={height}
                />
              )}
              {!this.state.approvalNav && (
                <ApprovalListingGrid
                  medClosed={
                    !this.state.medOpen &&
                    !this.state.ordOpen &&
                    !this.state.ordBulkOpen
                  }
                  openPhysicianBulkPrint={this.openPhysicianBulkPrint}
                  openOrderWindow={this.openOrderWindow}
                  siteId={siteId}
                  userId={userId}
                  userNumber={userNumber}
                  enableClinicalLinks={enableClinicalLinks}
                  hbcLocation={hbcLocation}
                  isMobile={isMobile}
                  openCellWindow={this.openCellWindow}
                  windowHeight={windowHeight}
                  height={height}
                  refresh={refresh}
                  handleCertRefresh={this.handleCertRefresh}
                  approvalGirdOpen={!this.state.approvalNav}
                  theme={this.props.theme}
                  {...this.props}
                />
              )}
              {!this.state.reviewNav && (
                <ReviewListingGrid
                  medClosed={
                    !this.state.medOpen &&
                    !this.state.ordOpen &&
                    !this.state.ordBulkOpen
                  }
                  siteId={siteId}
                  userId={userId}
                  userNumber={userNumber}
                  enableClinicalLinks={enableClinicalLinks}
                  hbcLocation={hbcLocation}
                  isMobile={isMobile}
                  openCellWindow={this.openReviewWindow}
                  windowHeight={windowHeight}
                  height={height}
                  refresh={refresh}
                  handleCertRefresh={this.handleCertRefresh}
                  approvalGirdOpen={!this.state.approvalNav}
                  theme={this.props.theme}
                  {...this.props}
                ></ReviewListingGrid>
              )}
            </Suspense>
          )}
        </HeightProviderWithWindowHeight>
        <Dialog
          classes={{
            root: `${classes.modalRoot} ${
              isMobile ? classes.modalRootMobile : ''
            }`,
            paperFullScreen: classes.loaderPaper,
            paperWidthLg: classes.modal,
            paperWidthFalse: classes.modalWidthFalse,
          }}
          maxWidth={this.state.certLoaded ? 'lg' : false}
          fullScreen={this.state.fullScreenDialog}
          open={this.state.certOpen}
          onClose={this.handleCertClose}
        >
          <div
            id="cert-wrap"
            style={
              isMobile
                ? { overflow: 'hidden', ...baseCertWrapStyle }
                : baseCertWrapStyle
            }
          >
            <PatientCert
              key={`cert-modal-${this.state.selectedPatientId}`}
              Context={certContext}
              getAgency={getAgency}
              maxWidth={modalMaxWidth}
              logout={logout}
              patientNumber={this.state.selectedPatientId}
              insuranceCarrierId={this.state.selectedPatientCarrierId}
              electionNumber={this.state.selectedPatientElectionNumber}
              onCancel={this.handleCertClose}
              onDataLoad={this.onCertLoad}
              updateUserInfo={updateUserInfo}
              showClose
            />
          </div>
        </Dialog>
        <Dialog
          open={this.state.ordBulkOpen && !this.state.bulkPrintReady}
          onClose={this.handleMedClose}
        >
          <DialogContent
            style={{
              textAlign: 'center',
              padding: 32,
              minWidth: 280,
            }}
          >
            <CircularProgress />
            <Box mt={2}>
              <Typography variant="body1">
                {this.props.intl.formatMessage(messages.bulkPrintPreparing)}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
        <DialogBox
          top
          open={this.state.medOpen}
          fullWidth
          maxWidth="xl"
          handleClick={this.handleMedClose}
          minHeight="50vh"
          title={this.state.medTitle}
          disablePrint
        >
          {this.state.medOpen ? (
            <PNCounterContextProvider
              keycloak={keycloak}
              agency={agency || 'demo1'}
              patientNumber={this.state.selectedPatientId}
              entityTypes={[ENTITY_MEDICATION]}
            >
              <ClearScripts
                embedded
                maintainMed
                online
                dashboardEmbedded={false}
                patientNumber={this.state.selectedPatientId}
                keycloak={keycloak}
                logout={logout}
                dateFrom={undefined}
                dateTo={undefined}
                agency={agency}
                isDoctor={isDoctor}
                onError={undefined}
                selectedDoctor={this.state.selectedDoctor}
                siteId={siteId}
                fromCERT
              />
            </PNCounterContextProvider>
          ) : null}
        </DialogBox>
        <UnsignedVerbalsPrintFaxBridge
          keycloak={keycloak}
          agency={agency}
          currentUser={currentUser}
          isDoctor={isDoctor}
          intl={this.props.intl}
          ordOpen={this.state.ordOpen}
          ordBulkOpen={this.state.ordBulkOpen}
          bulkPrintReady={this.state.bulkPrintReady}
          unsignedVerbalsPayload={this.state.unsignedVerbalsPayload}
          loadedBulkVerbals={this.state.loadedBulkVerbals}
          printReady={this.state.printReady}
          printServiceBusy={this.state.printServiceBusy}
          setPrintServiceBusy={this.setUnsignedVerbalsPrintBusy}
        >
          {({ onPrint, onFax, onEmail, disablePrint }) => (
            <DialogBox
              top
              open={
                this.state.ordOpen ||
                (this.state.ordBulkOpen && this.state.bulkPrintReady)
              }
              fullWidth={this.state.ordOpen}
              maxWidth={this.state.ordOpen ? 'md' : 'sm'}
              PaperProps={
                this.state.ordBulkOpen &&
                this.state.bulkPrintReady &&
                !this.state.ordOpen
                  ? { style: { maxWidth: 680 } }
                  : undefined
              }
              handleClick={this.handleMedClose}
              minHeight={this.state.ordOpen ? '50vh' : undefined}
              title={
                this.state.ordTitle ||
                (this.state.ordBulkOpen &&
                  this.state.bulkPrintReady &&
                  this.props.intl.formatMessage(messages.bulkPrintDialogTitle, {
                    doctorName: this.state.bulkPhysicianName,
                  }))
              }
              onPrint={onPrint}
              onFax={onFax}
              onEmail={onEmail}
              disablePrint={disablePrint}
            >
              <Box
                position="relative"
                minHeight={this.state.ordOpen ? '40vh' : undefined}
              >
                {this.state.printServiceBusy ? (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    zIndex={10}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Loader contained />
                  </Box>
                ) : null}
                {this.state.ordOpen ? (
                  <ApprovalContainer
                    patientNumber={this.state.selectedPatientId}
                    physicianPersonId={this.state.selectedDoctor}
                    approvalId={this.state.selectedApprovalId}
                    userId={userId}
                    userNumber={userNumber}
                    workflow={this.props.workflow}
                    observation={observation}
                    isDoctor={isDoctor}
                    isNp={this.props.isNP}
                    theme={this.props.theme}
                    hospiceBin={hospiceBin}
                    firstName={this.props.firstName}
                    lastName={this.props.lastName}
                    userCredentials={this.props.userCredentials}
                    {...this.props}
                  />
                ) : null}
                {this.state.ordBulkOpen && this.state.bulkPrintReady ? (
                  <Box p={2}>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {this.props.intl.formatMessage(
                        messages.bulkPrintReadyHint,
                      )}
                    </Typography>
                    <BulkUnsignedVerbalsSummaryTable
                      verbals={this.state.loadedBulkVerbals || []}
                      siteTimezone={this.props.currentUser?.siteTimezone}
                    />
                  </Box>
                ) : null}
              </Box>
            </DialogBox>
          )}
        </UnsignedVerbalsPrintFaxBridge>
        <DialogBox
          maxWidth="lg"
          fullScreen={this.state.fullScreenDialog}
          open={this.state.reviewOpen}
          handleClick={this.handleReviewClose}
          title={`Review Item: ${this.state.patientName}`}
        >
          {this.state.reviewOpen ? (
            <Fragment>
              <ReviewContainer
                workflow={this.props.workflow}
                observation={observation}
                siteId={siteId}
                theme={this.props.theme}
                patientId={this.state.selectedPatientId}
                patientNumber={this.state.selectedPatientNumber}
                reviewsMapKey={this.state.selectedReviewsMapKey}
                firstName={this.props.firstName}
                lastName={this.props.lastName}
                userCredentials={this.props.userCredentials}
                hospiceBin={hospiceBin}
              />
            </Fragment>
          ) : null}
        </DialogBox>
        {rxLink && (
          <RxModal
            open={rxModalOpen}
            toggleModal={this.closeRxModal}
            link={rxLink}
            isMobile={isMobile}
          />
        )}
      </div>
    );
  }
}

export default withApprovalContext(
  withReviewContext(
    injectIntl(withStyles(styles, { withTheme: true })(Listing)),
  ),
);
